import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';
import jsPDF from 'jspdf';

interface Platform {
  id: string;
  name: string;
  connected: boolean;
  inactive?: boolean;
}

interface ShareableReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analyticsData: any;
  platforms: Platform[];
}

export const ShareableReportModal: React.FC<ShareableReportModalProps> = ({ 
  open, 
  onOpenChange, 
  analyticsData,
  platforms
}) => {
  const [reportTitle, setReportTitle] = useState('Analytics Report');
  const [reportDescription, setReportDescription] = useState('Comprehensive social media analytics and engagement metrics');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(
    platforms.filter(p => p.connected).map(p => p.id)
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();
  const { currentUser } = useUser();

  const togglePlatform = (platformId: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
  };

  const handleGenerate = async () => {
    if (selectedPlatforms.length === 0) {
      toast({ title: "Select at least one platform", variant: "destructive" });
      return;
    }

    setIsGenerating(true);

    try {
      const stats = analyticsData?.weeklyStats;
      const platformNames = selectedPlatforms.map(id => platforms.find(p => p.id === id)?.name || id);

      // Generate PDF
      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.text(reportTitle, 20, 25);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 33);
      if (reportDescription) {
        doc.setFontSize(11);
        doc.setTextColor(60);
        doc.text(reportDescription, 20, 42, { maxWidth: 170 });
      }
      doc.setTextColor(0);
      let y = 55;
      doc.setFontSize(14);
      doc.text('Platforms Included', 20, y);
      y += 8;
      doc.setFontSize(11);
      doc.text(platformNames.join(', '), 20, y);
      y += 14;
      doc.setFontSize(14);
      doc.text('Summary Metrics', 20, y);
      y += 10;
      doc.setFontSize(11);
      if (stats) {
        doc.text(`Followers: ${(stats.followers?.current || 0).toLocaleString()} (${stats.followers?.change >= 0 ? '+' : ''}${stats.followers?.change || 0}%)`, 20, y); y += 7;
        doc.text(`Engagement Rate: ${(stats.engagement?.current || 0).toFixed(1)}% (${stats.engagement?.change >= 0 ? '+' : ''}${(stats.engagement?.change || 0).toFixed(1)}%)`, 20, y); y += 7;
        doc.text(`Reach: ${(stats.reach?.current || 0).toLocaleString()} (${stats.reach?.change >= 0 ? '+' : ''}${stats.reach?.change || 0}%)`, 20, y); y += 7;
        doc.text(`Total Likes: ${(stats.likes?.current || 0).toLocaleString()} (${stats.likes?.change >= 0 ? '+' : ''}${stats.likes?.change || 0}%)`, 20, y); y += 7;
      }
      doc.save(`${reportTitle.replace(/\s+/g, '_')}.pdf`);

      // Save to database
      if (currentUser) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', currentUser.id)
          .single();

        if (profile) {
          await supabase
            .from('analytics_reports' as any)
            .insert({
              profile_id: profile.id,
              title: reportTitle,
              description: reportDescription,
              platforms: selectedPlatforms,
              report_data: { weeklyStats: stats, chartData: analyticsData?.chartData },
            } as any);
        }
      }

      toast({
        title: "Report Generated",
        description: "Your PDF report has been downloaded and saved.",
      });

      onOpenChange(false);
    } catch (err) {
      console.error('Report generation error:', err);
      toast({ title: "Failed to generate report", variant: "destructive" });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Generate Report</DialogTitle>
          <DialogDescription>
            Create a professional analytics PDF to share with brands and sponsors
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Report Title</Label>
            <Input
              id="title"
              value={reportTitle}
              onChange={(e) => setReportTitle(e.target.value)}
              placeholder="Enter report title"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              placeholder="Brief description of the report"
              className="min-h-[80px]"
            />
          </div>

          <div className="space-y-2">
            <Label>Include Platforms</Label>
            <div className="space-y-2">
              {platforms.map((platform) => (
                <div key={platform.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`platform-${platform.id}`}
                    checked={selectedPlatforms.includes(platform.id)}
                    onCheckedChange={() => togglePlatform(platform.id)}
                    disabled={platform.inactive && !platform.connected}
                  />
                  <label
                    htmlFor={`platform-${platform.id}`}
                    className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed ${
                      platform.inactive && !platform.connected ? 'text-muted-foreground' : ''
                    }`}
                  >
                    {platform.name}
                    {platform.inactive && !platform.connected && (
                      <span className="text-xs text-muted-foreground ml-1">(Inactive)</span>
                    )}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <Button 
            onClick={handleGenerate} 
            disabled={isGenerating || selectedPlatforms.length === 0}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
